import React, { useEffect, useMemo, useState } from 'react';
import {
  adminClientsService,
  ClientDomain,
  ClientActiveConfig,
} from '../../../services/adminClientsService';
import { catalogService } from '../../../services/catalogService';
import { domainsService } from '../../../services/domainsService';
import { toastSuccess, toastError } from '../../../lib/toast';
import type { Prompt, ExtractionSchema, LLMConfig, OCRConfig } from '../../../types';
import { SystemTenantNotice } from './SystemTenantNotice';
import { ApiCredentialsPanel } from './ApiCredentialsPanel';

interface DomainRow {
  code: string;
  displayName: string;
}

interface DomainState {
  activated: boolean;
  prompt_id: string | null;
  extraction_schema_id: string | null;
  ocr_config_id: string | null;
  llm_config_id: string | null;
}

interface Props {
  clientId: string;
  isSystem?: boolean;
}

const filterByDomain = <T extends { domain: string }>(items: T[], domain: string): T[] =>
  items.filter((it) => it.domain === domain);

const labelFor = <T extends { name: string; version: string }>(it: T) => `${it.name} (v${it.version})`;

export const DomainsConfigsTab: React.FC<Props> = ({ clientId, isSystem = false }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [domains, setDomains] = useState<DomainRow[]>([]);
  const [initialState, setInitialState] = useState<Map<string, DomainState>>(new Map());
  const [domainsState, setDomainsState] = useState<Map<string, DomainState>>(new Map());

  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [schemas, setSchemas] = useState<ExtractionSchema[]>([]);
  const [ocrConfigs, setOcrConfigs] = useState<OCRConfig[]>([]);
  const [llmConfigs, setLlmConfigs] = useState<LLMConfig[]>([]);

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [
        clientDomains,
        activeConfigs,
        allDomainOptions,
        promptsData,
        schemasData,
        ocrData,
        llmData,
      ] = await Promise.all([
        adminClientsService.listDomains(clientId),
        adminClientsService.listActiveConfigs(clientId),
        domainsService.getDomainOptions(),
        catalogService.listPrompts(),
        catalogService.listSchemas(),
        catalogService.listOcrConfigs(),
        catalogService.listLlmConfigs(),
      ]);

      const codeFromClient = new Set(clientDomains.map((d) => d.domain_code));
      const labelByCode = new Map(allDomainOptions.map((o) => [o.value, o.label]));

      const allCodes = new Set<string>([
        ...allDomainOptions.map((o) => o.value),
        ...Array.from(codeFromClient),
      ]);

      const domainRows: DomainRow[] = Array.from(allCodes)
        .sort((a, b) => a.localeCompare(b))
        .map((code) => ({ code, displayName: labelByCode.get(code) || code }));

      const activeByCode = new Map<string, ClientActiveConfig>(
        activeConfigs.map((c) => [c.domain_code, c])
      );
      const activatedByCode = new Map<string, boolean>(
        clientDomains.map((d: ClientDomain) => [d.domain_code, d.is_active])
      );

      const stateMap = new Map<string, DomainState>();
      for (const row of domainRows) {
        const ac = activeByCode.get(row.code);
        stateMap.set(row.code, {
          activated: activatedByCode.get(row.code) ?? false,
          prompt_id: ac?.prompt_id ?? null,
          extraction_schema_id: ac?.extraction_schema_id ?? null,
          ocr_config_id: ac?.ocr_config_id ?? null,
          llm_config_id: ac?.llm_config_id ?? null,
        });
      }

      setDomains(domainRows);
      setInitialState(new Map(stateMap));
      setDomainsState(stateMap);
      setPrompts(promptsData);
      setSchemas(schemasData);
      setOcrConfigs(ocrData);
      setLlmConfigs(llmData);
    } catch (err: any) {
      setError(err?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const dirtyCodes = useMemo(() => {
    const dirty = new Set<string>();
    for (const [code, state] of domainsState.entries()) {
      const init = initialState.get(code);
      if (!init) {
        dirty.add(code);
        continue;
      }
      if (
        init.activated !== state.activated ||
        init.prompt_id !== state.prompt_id ||
        init.extraction_schema_id !== state.extraction_schema_id ||
        init.ocr_config_id !== state.ocr_config_id ||
        init.llm_config_id !== state.llm_config_id
      ) {
        dirty.add(code);
      }
    }
    return dirty;
  }, [domainsState, initialState]);

  const isDirty = dirtyCodes.size > 0;

  const updateState = (code: string, patch: Partial<DomainState>) => {
    setDomainsState((prev) => {
      const next = new Map(prev);
      const cur = next.get(code);
      if (!cur) return prev;
      next.set(code, { ...cur, ...patch });
      return next;
    });
  };

  const handleSave = async () => {
    if (!isDirty) return;
    setSaving(true);
    try {
      // 1) Activation diffs → batch setDomains if any activation changed
      const activationChanged = Array.from(dirtyCodes).some((code) => {
        const init = initialState.get(code);
        const cur = domainsState.get(code);
        return init && cur && init.activated !== cur.activated;
      });

      if (activationChanged) {
        const activatedCodes: string[] = [];
        for (const [code, state] of domainsState.entries()) {
          if (state.activated) activatedCodes.push(code);
        }
        await adminClientsService.setDomains(clientId, activatedCodes);
      }

      // 2) Config diffs per domain (only if activated AND configs changed)
      const configCalls: Promise<unknown>[] = [];
      for (const code of dirtyCodes) {
        const init = initialState.get(code);
        const cur = domainsState.get(code);
        if (!cur || !cur.activated) continue;
        const cfgChanged =
          !init ||
          init.prompt_id !== cur.prompt_id ||
          init.extraction_schema_id !== cur.extraction_schema_id ||
          init.ocr_config_id !== cur.ocr_config_id ||
          init.llm_config_id !== cur.llm_config_id;
        if (!cfgChanged) continue;
        if (
          !cur.prompt_id ||
          !cur.extraction_schema_id ||
          !cur.ocr_config_id ||
          !cur.llm_config_id
        ) {
          // Skip incomplete (do not send NULLs)
          continue;
        }
        configCalls.push(
          adminClientsService.setActiveConfig(clientId, code, {
            prompt_id: cur.prompt_id,
            extraction_schema_id: cur.extraction_schema_id,
            ocr_config_id: cur.ocr_config_id,
            llm_config_id: cur.llm_config_id,
          })
        );
      }
      await Promise.all(configCalls);

      toastSuccess(`${dirtyCodes.size} domaine(s) mis à jour`);
      await loadAll();
    } catch (err) {
      toastError('Enregistrement échoué', err);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setDomainsState(new Map(initialState));
  };

  if (loading) {
    return <div className="text-gray-500 py-8 text-center">Chargement...</div>;
  }
  if (error) {
    return <div className="text-red-600 py-8 text-center">{error}</div>;
  }

  return (
    <div className="space-y-4">
      {isSystem && <SystemTenantNotice what="aucun domaine métier sur ce tenant" />}
      <div className="text-sm text-gray-600 mb-2">
        Activation des domaines pour ce client et configurations actives (Schéma, Prompt, OCR, LLM).
      </div>

      <div className="space-y-3">
        {domains.map((row) => {
          const state = domainsState.get(row.code);
          if (!state) return null;
          const isDirtyRow = dirtyCodes.has(row.code);

          const promptOpts = filterByDomain(prompts, row.code);
          const schemaOpts = filterByDomain(schemas, row.code);
          const ocrOpts = filterByDomain(ocrConfigs, row.code);
          const llmOpts = filterByDomain(llmConfigs, row.code);

          const dropdownDisabled = !state.activated || isSystem;

          return (
            <div
              key={row.code}
              className={`border rounded p-4 ${isDirtyRow ? 'border-amber-400 bg-amber-50' : 'border-gray-200 bg-white'}`}
            >
              <div className="flex items-center justify-between mb-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={state.activated}
                    disabled={isSystem}
                    onChange={(e) => updateState(row.code, { activated: e.target.checked })}
                    className="rounded border-gray-300 text-primary"
                  />
                  <span className="font-medium text-gray-900">{row.displayName}</span>
                  <span className="text-xs text-gray-500">({row.code})</span>
                </label>
                {isDirtyRow && (
                  <span className="text-xs text-amber-700">Modifié</span>
                )}
              </div>

              <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 ${dropdownDisabled ? 'opacity-50 pointer-events-none' : ''}`}>
                <DropdownField
                  label="Schéma"
                  value={state.extraction_schema_id}
                  onChange={(v) => updateState(row.code, { extraction_schema_id: v })}
                  options={schemaOpts.map((o) => ({ value: o.id, label: labelFor(o) }))}
                  disabled={dropdownDisabled}
                />
                <DropdownField
                  label="Prompt"
                  value={state.prompt_id}
                  onChange={(v) => updateState(row.code, { prompt_id: v })}
                  options={promptOpts.map((o) => ({ value: o.id, label: labelFor(o) }))}
                  disabled={dropdownDisabled}
                />
                <DropdownField
                  label="OCR"
                  value={state.ocr_config_id}
                  onChange={(v) => updateState(row.code, { ocr_config_id: v })}
                  options={ocrOpts.map((o) => ({ value: o.id, label: labelFor(o) }))}
                  disabled={dropdownDisabled}
                />
                <DropdownField
                  label="LLM"
                  value={state.llm_config_id}
                  onChange={(v) => updateState(row.code, { llm_config_id: v })}
                  options={llmOpts.map((o) => ({ value: o.id, label: labelFor(o) }))}
                  disabled={dropdownDisabled}
                />
              </div>

              <div className="mt-2">
                <span
                  className="text-xs text-gray-400 italic"
                  title="Disponible en Phase 2 si défauts configurés sur le domaine"
                >
                  Réinitialiser aux défauts (Phase 2)
                </span>
              </div>

              {/* M23 — Clés API LLM par (client, domain) */}
              {state.activated && !isSystem && (
                <ApiCredentialsPanel
                  clientId={clientId}
                  domainCode={row.code}
                  disabled={isSystem}
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <button
          type="button"
          onClick={handleReset}
          disabled={!isDirty || saving}
          className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
        >
          Annuler
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!isDirty || saving || isSystem}
          className="px-4 py-2 text-sm bg-primary text-white rounded hover:bg-primary-600 disabled:opacity-50"
        >
          {saving ? 'Enregistrement...' : `Enregistrer${isDirty ? ` (${dirtyCodes.size})` : ''}`}
        </button>
      </div>
    </div>
  );
};

interface DropdownFieldProps {
  label: string;
  value: string | null;
  onChange: (v: string | null) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
}

const DropdownField: React.FC<DropdownFieldProps> = ({ label, value, onChange, options, disabled }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value || null)}
      disabled={disabled}
      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white disabled:bg-gray-100"
    >
      <option value="">— Sélectionner —</option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
      {value && !options.find((o) => o.value === value) && (
        <option value={value}>(valeur courante non listée)</option>
      )}
    </select>
  </div>
);

export default DomainsConfigsTab;
